/* eslint-disable prettier/prettier */
import React, { useState } from 'react';
import styles from '../../../styles/components/pageComponents/program/EditProgramFaq.module.css'; // Adjust the import path as needed

interface FAQ {
	query: string;
	response: string;
}

interface FaqModalProps {
	onClose: () => void;
	onSave?: (faqs: FAQ[]) => void;
	initialFaqs?: FAQ[]; // Optional: Pass existing FAQs if editing
}

const FaqModal: React.FC<FaqModalProps> = ({
	onClose,
	onSave,
	initialFaqs,
}) => {
	// Initialize with 3 empty FAQs to match the screenshot, or use provided ones
	const [faqs, setFaqs] = useState<FAQ[]>(
		initialFaqs?.length
			? initialFaqs
			: [
					{ query: '', response: '' },
					{ query: '', response: '' },
					{ query: '', response: '' },
				]
	);

	const [isSaving, setIsSaving] = useState(false);

	const handleFaqChange = (
		index: number,
		field: keyof FAQ,
		value: string
	) => {
		const updatedFaqs = [...faqs];
		updatedFaqs[index][field] = value;
		setFaqs(updatedFaqs);
	};

	const handleAddFaq = () => {
		setFaqs([...faqs, { query: '', response: '' }]);
	};

	const handleSaveChanges = async () => {
		setIsSaving(true);
		try {
			// Filter out entirely empty FAQs before saving
			const validFaqs = faqs.filter(
				faq => faq.query.trim() !== '' || faq.response.trim() !== ''
			);

			if (onSave) {
				await onSave(validFaqs);
			}
			onClose();
		} catch (error) {
			console.error('Failed to save FAQs', error);
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<div className={styles.modalOverlay}>
			<div className={styles.modalContainer}>
				{/* Header */}
				<div className={styles.modalHeader}>
					<h2 className={styles.modalTitle}>
						Update Program Details – FAQs
					</h2>
					<div className={styles.headerActions}>
						<button
							className={styles.btnCancel}
							onClick={onClose}
							disabled={isSaving}
						>
							Cancel
						</button>
						<button
							className={styles.btnSave}
							onClick={handleSaveChanges}
							disabled={isSaving}
						>
							{isSaving ? 'Saving...' : 'Save Changes'}
						</button>
					</div>
				</div>

				{/* Body */}
				<div className={styles.modalBody}>
					<h3 className={styles.sectionTitle}>FAQs</h3>

					<div className={styles.faqList}>
						{faqs.map((faq, index) => (
							<div key={index} className={styles.faqItem}>
								<label className={styles.faqLabel}>
									FAQ {index + 1}
								</label>
								<input
									type="text"
									className={styles.input}
									placeholder="Query"
									value={faq.query}
									onChange={e =>
										handleFaqChange(
											index,
											'query',
											e.target.value
										)
									}
								/>
								<input
									type="text"
									className={styles.input}
									placeholder="Response"
									value={faq.response}
									onChange={e =>
										handleFaqChange(
											index,
											'response',
											e.target.value
										)
									}
								/>
							</div>
						))}
					</div>

					<button className={styles.addFaqBtn} onClick={handleAddFaq}>
						Add New FAQ <span className={styles.plusIcon}>+</span>
					</button>
				</div>
			</div>
		</div>
	);
};

export default FaqModal;
